import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ExpandMore } from "@mui/icons-material";
import {
  FDP_COLUMN_NAME,
  FDP_FILTER_COLUMN_OPTIONS,
  FDP_LABEL_NAME,
} from "./FdpConstants";
import AutoCompleteFilter from "../../components/common/autoCompleteFilter/AutoCompleteFilter";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import { FdpResponse } from "../../app/models/responseModels/FdpResponse";

type FdpDataFilterProps = {
  fdpData: FdpResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<FdpResponse[]>>;
  filteredData: FdpResponse[];
};
type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const FdpDataFilter = ({
  fdpData,
  setFilteredData,
  filteredData,
}: FdpDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = React.useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [companyNameOptions, setCompanyNameOptions] = useState<string[]>([]); //Dropdown Options
  const [companyNameSelectedOptions, setCompanyNameSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [dispatcherNameOptions, setDispatcherNameOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [dispatcherNameSelectedOptions, setDispatcherNameSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [activeOptions, setAactiveOptions] = useState<string[]>([]); //Dropdown Options
  const [activeSelectedOptions, setAactiveSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [emailOptions, setEmailOptions] = useState<string[]>([]); //Dropdown Options
  const [emailSelectedOptions, setEmailSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [primaryMsaOptions, setPrimaryMsaOptions] = useState<string[]>([]); //Dropdown Options
  const [primaryMsaSelectedOptions, setPrimaryMsaSelectedOptions] =
    useState<string[]>([]);

  const [primaryPhoneOptions, setPrimaryPhoneOptions] = useState<string[]>([]); //Dropdown Options
  const [primaryPhoneSelectedOptions, setPrimaryPhoneSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [cityOptions, setCityOptions] = useState<string[]>([]); //Dropdown Options
  const [citySelectedOptions, setCitySelectedOptions] = useState<string[]>([]); //Dropdown selected value

  const [stateOptions, setStateOptions] = useState<string[]>([]); //Dropdown Options
  const [stateSelectedOptions, setStateSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [zipCodeOptions, setZipCodeOptions] = useState<string[]>([]); //Dropdown Options
  const [zipCodeSelectedOptions, setZipCodeSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value
  const [filteredOptions, setFilteredOptions] = useState<FdpResponse[]>([]); //Dropdown Options

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === FDP_COLUMN_NAME.COMAPANY_NAME) {
        return companyNameOptions;
      }
      if (columnName === FDP_COLUMN_NAME.DISPATCHER_NAME) {
        return dispatcherNameOptions;
      }
      if (columnName === FDP_COLUMN_NAME.EMAIL) {
        return emailOptions;
      }
      if (columnName === FDP_COLUMN_NAME.PRIMARY_PHONE) {
        return primaryPhoneOptions;
      }
      if (columnName === FDP_COLUMN_NAME.PRIMARY_MSA) {
        return primaryMsaOptions;
      }
      if (columnName === FDP_COLUMN_NAME.ACTIVE) {
        return activeOptions;
      }
      if (columnName === FDP_COLUMN_NAME.CITY) {
        return cityOptions;
      }
      if (columnName === FDP_COLUMN_NAME.STATE) {
        return stateOptions;
      }
      if (columnName === FDP_COLUMN_NAME.ZIP_CODE) {
        return zipCodeOptions;
      }
      return [];
    },
    [
      companyNameOptions,
      dispatcherNameOptions,
      emailOptions,
      primaryPhoneOptions,
      primaryMsaOptions,
      activeOptions,
      cityOptions,
      stateOptions,
      zipCodeOptions,
    ]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === FDP_COLUMN_NAME.COMAPANY_NAME) {
        return companyNameSelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.DISPATCHER_NAME) {
        return dispatcherNameSelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.EMAIL) {
        return emailSelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.PRIMARY_PHONE) {
        return primaryPhoneSelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.PRIMARY_MSA) {
        return primaryMsaSelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.ACTIVE) {
        return activeSelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.CITY) {
        return citySelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.STATE) {
        return stateSelectedOptions;
      }
      if (columnName === FDP_COLUMN_NAME.ZIP_CODE) {
        return zipCodeSelectedOptions;
      }
      return [];
    },
    [
      companyNameSelectedOptions,
      dispatcherNameSelectedOptions,
      emailSelectedOptions,
      primaryPhoneSelectedOptions,
      primaryMsaSelectedOptions,
      activeSelectedOptions,
      citySelectedOptions,
      stateSelectedOptions,
      zipCodeSelectedOptions,
    ]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const firstSelectedColumnName = inputList[0].columnName;
    const lastSelectedColumnName = inputList[inputList.length - 1].columnName;

    setFilteredOptions(filteredData);
    //UserName dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.COMAPANY_NAME) {
      setCompanyNameOptions(
        fdpData
          .filter((v) => v.companyName)
          .map((v) => v.companyName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.COMAPANY_NAME) {
      setCompanyNameOptions(
        filteredOptions
          .filter((v) => v.companyName)
          .map((v) => v.companyName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    // dispatcher name dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.DISPATCHER_NAME) {
      setDispatcherNameOptions(
        fdpData
          .filter((v) => v.dispatcherName)
          .map((v) => v.dispatcherName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.DISPATCHER_NAME) {
      setDispatcherNameOptions(
        filteredOptions
          .filter((v) => v.dispatcherName)
          .map((v) => v.dispatcherName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Email dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.EMAIL) {
      setEmailOptions(
        fdpData
          .filter((v) => v.email)
          .map((v) => v.email)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.EMAIL) {
      setEmailOptions(
        filteredOptions
          .filter((v) => v.email)
          .map((v) => v.email)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Phone dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.PRIMARY_PHONE) {
      setPrimaryPhoneOptions(
        fdpData
          .filter((v) => v.primaryPhone)
          .map((v) => v.primaryPhone)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.PRIMARY_PHONE) {
      setPrimaryPhoneOptions(
        filteredOptions
          .filter((v) => v.primaryPhone)
          .map((v) => v.primaryPhone)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Primary Msa dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.PRIMARY_MSA) {
      setPrimaryMsaOptions(
        fdpData
          .filter((v) => v.primaryMsa)
          .map((v) => v.primaryMsa)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.PRIMARY_MSA) {
      setPrimaryMsaOptions(
        filteredOptions
          .filter((v) => v.primaryMsa)
          .map((v) => v.primaryMsa)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Deleted dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.ACTIVE) {
      setAactiveOptions(
        fdpData
          .map((v) => (v.active ? "True" : "False"))
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setAactiveOptions(
        filteredOptions
          .map((v) => (v.active ? "True" : "False"))
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //City dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.CITY) {
      setCityOptions(
        fdpData
          .filter((v) => v.city)
          .map((v) => v.city)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.CITY) {
      setCityOptions(
        filteredOptions
          .filter((v) => v.city)
          .map((v) => v.city)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //City dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.STATE) {
      setStateOptions(
        fdpData
          .filter((v) => v.state)
          .map((v) => v.state)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.STATE) {
      setStateOptions(
        filteredOptions
          .filter((v) => v.state)
          .map((v) => v.state)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //City dropdown options
    if (firstSelectedColumnName === FDP_COLUMN_NAME.ZIP_CODE) {
      setZipCodeOptions(
        fdpData
          .filter((v) => v.zipCode)
          .map((v) => v.zipCode)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else if (lastSelectedColumnName !== FDP_COLUMN_NAME.ZIP_CODE) {
      setZipCodeOptions(
        filteredOptions
          .filter((v) => v.zipCode)
          .map((v) => v.zipCode)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, fdpData]);

  useEffect(() => {
    let newData = [...fdpData];
    //UserName Filter
    if (companyNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return companyNameSelectedOptions.some((s) => {
          return s === a.companyName;
        });
      });
    }

    //Dispatcher name Filter
    if (dispatcherNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return dispatcherNameSelectedOptions.some((s) => {
          return s === a.dispatcherName;
        });
      });
    }

    //Email Filter
    if (emailSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return emailSelectedOptions.some((s) => {
          return s === a.email;
        });
      });
    }

    //Phone Filter
    if (primaryPhoneSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return primaryPhoneSelectedOptions.some((s) => {
          return s === a.primaryPhone;
        });
      });
    }

    //Primary Msa Filter
    if (primaryMsaSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return primaryMsaSelectedOptions.some((s) => {
          return s === a.primaryMsa;
        });
      });
    }

    //active Filter
    if (activeSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return activeSelectedOptions.some((s) => {
          return s === (a.active ? "True" : "False");
        });
      });
    }

    //City Filter
    if (citySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return citySelectedOptions.some((s) => {
          return s === a.city;
        });
      });
    }

    //State Filter
    if (stateSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return stateSelectedOptions.some((s) => {
          return s === a.state;
        });
      });
    }

    //Zipcode Filter
    if (zipCodeSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return zipCodeSelectedOptions.some((s) => {
          return s === a.zipCode;
        });
      });
    }
    setFilteredData([...newData]);
  }, [
    fdpData,
    setFilteredData,
    companyNameSelectedOptions,
    dispatcherNameSelectedOptions,
    emailSelectedOptions,
    primaryPhoneSelectedOptions,
    primaryMsaSelectedOptions,
    activeSelectedOptions,
    citySelectedOptions,
    stateSelectedOptions,
    zipCodeSelectedOptions,
  ]);

  // ***** Handlers *****
  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === FDP_COLUMN_NAME.COMAPANY_NAME) {
      return setCompanyNameSelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.DISPATCHER_NAME) {
      return setDispatcherNameSelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.EMAIL) {
      return setEmailSelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.PRIMARY_PHONE) {
      return setPrimaryPhoneSelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.PRIMARY_MSA) {
      return setPrimaryMsaSelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.ACTIVE) {
      return setAactiveSelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.CITY) {
      return setCitySelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.STATE) {
      return setStateSelectedOptions([]);
    }
    if (removedColumnName === FDP_COLUMN_NAME.ZIP_CODE) {
      setZipCodeSelectedOptions([]);
      return;
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === FDP_COLUMN_NAME.COMAPANY_NAME) {
        setCompanyNameSelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.DISPATCHER_NAME) {
        setDispatcherNameSelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.EMAIL) {
        setEmailSelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.PRIMARY_PHONE) {
        setPrimaryPhoneSelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.PRIMARY_MSA) {
        setPrimaryMsaSelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.ACTIVE) {
        setAactiveSelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.CITY) {
        setCitySelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.STATE) {
        setStateSelectedOptions([]);
      }
      if (oldColumnName === FDP_COLUMN_NAME.ZIP_CODE) {
        setZipCodeSelectedOptions([]);
      }
    }
    if (value === FDP_COLUMN_NAME.COMAPANY_NAME) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.COMAPANY_NAME;
    }
    if (value === FDP_COLUMN_NAME.DISPATCHER_NAME) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.DISPATCHER_NAME;
    }
    if (value === FDP_COLUMN_NAME.EMAIL) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.EMAIL;
    }
    if (value === FDP_COLUMN_NAME.PRIMARY_PHONE) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.PRIMARY_PHONE;
    }
    if (value === FDP_COLUMN_NAME.PRIMARY_MSA) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.PRIMARY_MSA;
    }
    if (value === FDP_COLUMN_NAME.ACTIVE) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.ACTIVE;
    }
    if (value === FDP_COLUMN_NAME.CITY) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.CITY;
    }
    if (value === FDP_COLUMN_NAME.STATE) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.STATE;
    }
    if (value === FDP_COLUMN_NAME.ZIP_CODE) {
      newInputList[index].displayColumnName = FDP_LABEL_NAME.ZIP_CODE;
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === FDP_COLUMN_NAME.COMAPANY_NAME) {
      return setCompanyNameSelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.DISPATCHER_NAME) {
      return setDispatcherNameSelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.EMAIL) {
      return setEmailSelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.PRIMARY_PHONE) {
      return setPrimaryPhoneSelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.PRIMARY_MSA) {
      return setPrimaryMsaSelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.ACTIVE) {
      return setAactiveSelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.CITY) {
      return setCitySelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.STATE) {
      return setStateSelectedOptions(value);
    }
    if (columnName === FDP_COLUMN_NAME.ZIP_CODE) {
      return setZipCodeSelectedOptions(value);
    }
  };

  const handleReset = (e: any) => {
    setCompanyNameSelectedOptions([]);
    setDispatcherNameSelectedOptions([]);
    setEmailSelectedOptions([]);
    setPrimaryPhoneSelectedOptions([]);
    setPrimaryMsaSelectedOptions([]);
    setAactiveSelectedOptions([]);
    setCitySelectedOptions([]);
    setStateSelectedOptions([]);
    setZipCodeSelectedOptions([]);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Fdp</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {FDP_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <AutoCompleteFilter
                    columnName={inputList[index].columnName}
                    displayColumnName={inputList[index].displayColumnName}
                    options={getFilterOptions(inputList[index].columnName)}
                    value={getSelectedOptions(inputList[index].columnName)}
                    onChange={(event: any, selectedValue: string[]) => {
                      handleSelectedOption(
                        event,
                        inputList[index].columnName,
                        selectedValue
                      );
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default FdpDataFilter;
